import { useCallback } from 'react';
import { FileError, useDropzone } from 'react-dropzone';

import { ACCEPTED_FILE_TYPES, FileImage, MAX_SIZE_OF_FILE } from '@/common/constants';
import prepareFile from '@/components/controls/DropScan/prepareFile';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import useOnDrop from '@/components/controls/DropScan/useOnDrop';

export interface UseUploadDropzoneProps {
  onUpload?: () => void;
  onFileUploadError?: (error?: FileError) => void;
  storeKey?: string;
  multiple?: boolean;
  maxFiles?: number;
  disableFilesUpload?: boolean;
  ocrRequired?: boolean;
}

const useUploadDropzone = ({
  onUpload,
  onFileUploadError,
  storeKey,
  multiple,
  maxFiles,
  disableFilesUpload = true,
  ocrRequired = false,
}: UseUploadDropzoneProps) => {
  const {
    handleSetValues,
    values: { files: storedFiles },
  } = useFilesInDocumentStore(storeKey);

  const onFilesAdded = useCallback(
    (acceptedFiles: FileImage[]) => {
      if (acceptedFiles?.length) {
        if (disableFilesUpload) {
          handleSetValues({ files: [...(storedFiles || []), ...acceptedFiles.map(prepareFile)] });
        }
        onUpload?.();
      }
    },
    [handleSetValues, onUpload, storedFiles, disableFilesUpload]
  );

  const { onDrop, isLoading } = useOnDrop({
    onFilesAdded,
    onFileUploadError,
    disableFilesUpload,
    storeKey,
    ocrRequired,
  });

  const { getInputProps, open } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_SIZE_OF_FILE,
    onDrop,
    noDrag: true,
    noClick: true,
    multiple: multiple || false,
    maxFiles: maxFiles || 1,
  });

  return { open, getInputProps, isLoading };
};

export default useUploadDropzone;
